/**
 * Bottom-left agent dock.
 *
 * The whole point is the negative case: it does NOT animate when nothing is
 * happening. An idle pulse is the standard mistake — it looks alive, so people
 * stop reading it, and then the one time it means something nobody notices.
 * Here motion is evidence: the ring spins only while a tool is genuinely in
 * flight, and the dink fires once per completed action, driven by a nonce
 * rather than a timer.
 *
 * Sits bottom-LEFT so it never overlaps the Emjae bubble bottom-right.
 */
import { useEffect, useRef, useState } from 'react';
import { Bot } from 'lucide-react';

import { isAgentWorking, useAgentActivity } from '../agentActivity';
import { cn } from '@/lib/utils';

/** How long the one-shot dink lasts. Comfortably under a human "did that just move?". */
const DINK_MS = 900;

export function AgentActivityDock() {
  const phase = useAgentActivity((s) => s.phase);
  const tool = useAgentActivity((s) => s.tool);
  const line = useAgentActivity((s) => s.line);
  const dinkNonce = useAgentActivity((s) => s.dinkNonce);

  const [dinking, setDinking] = useState(false);
  const seenNonce = useRef(dinkNonce);

  // One shot per completed action. Ignoring the first render matters: mounting
  // after an earlier action must not replay someone else's dink.
  useEffect(() => {
    if (dinkNonce === seenNonce.current) return;
    seenNonce.current = dinkNonce;
    setDinking(true);
    const t = window.setTimeout(() => setDinking(false), DINK_MS);
    return () => window.clearTimeout(t);
  }, [dinkNonce]);

  const working = isAgentWorking(phase);
  const visible = working || dinking || phase === 'refused';

  // Nothing to say, nothing on screen. The dock is not a permanent ornament.
  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2" role="status" aria-live="polite">
      <span
        className={cn(
          'relative grid size-9 place-items-center rounded-full border shadow-sm transition-colors',
          phase === 'refused'
            ? 'border-destructive/40 bg-destructive/10 text-destructive'
            : 'border-teal/40 bg-paper text-teal-dark',
          dinking && 'agent-dock-dink',
        )}
      >
        <Bot className="size-4" aria-hidden="true" />
        {working && (
          <span
            className="absolute inset-0 rounded-full border-2 border-teal/30 border-t-teal animate-spin"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="max-w-[19rem] truncate rounded-md border border-warm-border bg-paper px-2.5 py-1.5 text-[12px] leading-snug text-ink shadow-sm">
        {line ?? (tool ? `${tool} running…` : 'Agent working…')}
      </span>
    </div>
  );
}
